import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import MapView, { Marker, NavigationControl, Popup } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import { useAuth } from "@/context/AuthContext";
import { buildLocationTelemetry } from "@/lib/location";
import Sidebar from "@/components/layout/Sidebar";
import DashboardDateTimeWidget from "@/components/layout/DashboardDateTimeWidget";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Activity, AlertTriangle, CheckCircle, Coffee, Gauge, LifeBuoy, LocateFixed, MapPin, Navigation, Power, Route as RouteIcon, ShieldCheck, Timer } from "lucide-react";
import {
  formatEmergencyDistance,
  formatEmergencyEta,
  getEmergencyReportMarkerClass,
  getEmergencyReportStatusBadgeClass,
  getEmergencyReportStatusLabel,
  getEmergencyReportTypeLabel,
  getEmergencyServiceBadgeClass,
  getEmergencyServiceMarkerClass,
  getEmergencyServiceStatusLabel,
  getEmergencyServiceTypeLabel,
  getEmergencySeverityBadgeClass,
  getEmergencySeverityLabel,
} from "@/lib/emergency";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;
const MAP_STYLE = "mapbox://styles/mapbox/navigation-night-v1";
const TRACKING_STORAGE_VERSION = 1;
const getTrackingStorageKey = (userId) => `eagle_emergency_tracking_${userId}`;
const getStatusStorageKey = (userId) => `eagle_emergency_last_status_${userId}`;

const responderStatusConfig = {
  available: { label: "Available", icon: CheckCircle, color: "text-emerald-400", bg: "bg-emerald-950/50" },
  on_duty: { label: "On Duty", icon: Activity, color: "text-primary", bg: "bg-primary/10" },
  off_duty: { label: "Off Duty", icon: Power, color: "text-zinc-400", bg: "bg-zinc-800" },
  on_break: { label: "On Break", icon: Coffee, color: "text-amber-400", bg: "bg-amber-950/50" },
};

const responderMapMarkerColor = {
  available: "#22c55e",
  on_duty: "#3b82f6",
  on_break: "#f59e0b",
  off_duty: "#94a3b8",
};

const formatDriveTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const haversineMiles = (a, b) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const earthRadiusMiles = 3958.8;
  const dLat = toRad(b.latitude - a.latitude);
  const dLng = toRad(b.longitude - a.longitude);
  const lat1 = toRad(a.latitude);
  const lat2 = toRad(b.latitude);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return earthRadiusMiles * c;
};

const EmergencyDashboard = () => {
  const { user } = useAuth();
  const [currentStatus, setCurrentStatus] = useState(user?.driver_status || "off_duty");
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isTracking, setIsTracking] = useState(false);
  const [trackingPermission, setTrackingPermission] = useState("unknown");
  const [liveSpeedMph, setLiveSpeedMph] = useState(0);
  const [driveTimeSec, setDriveTimeSec] = useState(0);
  const [milesDriven, setMilesDriven] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState(null);
  const [sessionStartedAt, setSessionStartedAt] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [mapViewState, setMapViewState] = useState({ latitude: 39.8283, longitude: -98.5795, zoom: 4 });
  const [emergencyReports, setEmergencyReports] = useState([]);
  const [emergencyServices, setEmergencyServices] = useState([]);
  const [emergencyRoutes, setEmergencyRoutes] = useState([]);
  const [driveHistory, setDriveHistory] = useState({ summary: {}, sessions: [] });
  const [selectedMapFeature, setSelectedMapFeature] = useState(null);
  const [responseDrafts, setResponseDrafts] = useState({});
  const [actionId, setActionId] = useState("");
  const [safeMode, setSafeMode] = useState(true);

  const watchIdRef = useRef(null);
  const lastCoordRef = useRef(null);
  const lastKnownCoordsRef = useRef(null);
  const driveTimeRef = useRef(0);
  const milesRef = useRef(0);
  const lastUpdateSentAtRef = useRef(0);
  const driveTimerRef = useRef(null);
  const statusRef = useRef(currentStatus);
  const autoPromptedRef = useRef(false);
  const currentPositionRef = useRef(null);
  const lastEmergencyFetchRef = useRef(0);

  useEffect(() => {
    statusRef.current = currentStatus;
  }, [currentStatus]);

  const fetchResponderHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/emergency/responder/history?limit=25`);
      const payload = response.data || { summary: {}, sessions: [] };
      setDriveHistory(payload);
      if (typeof payload.summary?.total_drive_time_seconds === "number") {
        const seconds = Math.max(0, Math.floor(payload.summary.total_drive_time_seconds));
        setDriveTimeSec(seconds);
        driveTimeRef.current = seconds;
      }
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load responder history");
    }
  }, []);

  const fetchEmergencyData = useCallback(async ({ force = false } = {}) => {
    const now = Date.now();
    if (!force && now - lastEmergencyFetchRef.current < 45000) return;
    lastEmergencyFetchRef.current = now;
    setDashboardLoading(true);
    try {
      const responderPosition = currentPositionRef.current;
      const [reportsResponse, servicesResponse, routesResponse] = await Promise.all([
        axios.get(`${API}/emergency/reports`, { params: { active_only: false } }),
        axios.get(`${API}/emergency/services`, {
          params: responderPosition
            ? {
                near_latitude: responderPosition.latitude,
                near_longitude: responderPosition.longitude,
                radius_miles: 120,
              }
            : undefined,
        }),
        axios.get(`${API}/emergency/routes`, { params: { limit: 12 } }),
      ]);
      setEmergencyReports(reportsResponse.data || []);
      setEmergencyServices(servicesResponse.data || []);
      setEmergencyRoutes(routesResponse.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to load emergency responder dashboard");
    } finally {
      setDashboardLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEmergencyData({ force: true });
    fetchResponderHistory();
  }, [fetchEmergencyData, fetchResponderHistory]);

  useEffect(() => {
    if (!currentPosition) return;
    fetchEmergencyData();
  }, [currentPosition, fetchEmergencyData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchEmergencyData({ force: true });
      fetchResponderHistory();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchEmergencyData, fetchResponderHistory]);

  const toLocationPayload = useCallback((coordsLike, speedMph) => buildLocationTelemetry({
    latitude: coordsLike.latitude,
    longitude: coordsLike.longitude,
    lat: coordsLike.latitude,
    lng: coordsLike.longitude,
    accuracy_meters: coordsLike.accuracy,
    speed_mph: speedMph,
    heading: coordsLike.heading,
    altitude: coordsLike.altitude,
    tracked_at: new Date().toISOString(),
    miles_driven_session: Number(milesRef.current.toFixed(2)),
    drive_time_seconds: driveTimeRef.current,
  }), []);

  const updateResponderStatus = async (status) => {
    setIsUpdating(true);
    try {
      const payload = { status };
      const known = lastKnownCoordsRef.current;
      const speed = Number(liveSpeedMph.toFixed(1));
      if (known?.latitude && known?.longitude) {
        payload.location = await toLocationPayload(known, speed);
      }
      await axios.put(`${API}/users/driver-status`, payload);
      setCurrentStatus(status);
      if (user?.id) localStorage.setItem(getStatusStorageKey(user.id), status);
      await fetchResponderHistory();
      toast.success(`Responder status updated to ${responderStatusConfig[status].label}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update responder status");
    } finally {
      setIsUpdating(false);
    }
  };

  const sendLocationUpdate = useCallback(async (coords, speedMph) => {
    const now = Date.now();
    if (now - lastUpdateSentAtRef.current < 2000) return;
    lastUpdateSentAtRef.current = now;
    try {
      const locationPayload = await toLocationPayload(coords, speedMph);
      await axios.put(`${API}/users/driver-status`, {
        status: statusRef.current,
        location: locationPayload,
      });
    } catch (_) {
      // Keep tracking non-blocking.
    }
  }, [toLocationPayload]);

  const stopTracking = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (driveTimerRef.current) {
      clearInterval(driveTimerRef.current);
      driveTimerRef.current = null;
    }
    setIsTracking(false);
    setLiveSpeedMph(0);
  }, []);

  const startTracking = useCallback(async ({ resetSession = true } = {}) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported on this device");
      return;
    }
    if (watchIdRef.current !== null) return;

    if (resetSession) {
      lastCoordRef.current = null;
      driveTimeRef.current = 0;
      milesRef.current = 0;
      setDriveTimeSec(0);
      setMilesDriven(0);
      setLiveSpeedMph(0);
      setSessionStartedAt(new Date().toISOString());
    }

    try {
      if (navigator.permissions?.query) {
        const permissionResult = await navigator.permissions.query({ name: "geolocation" });
        setTrackingPermission(permissionResult.state);
      }
    } catch (_) {
      // Some browsers do not support permissions API.
    }

    if (driveTimerRef.current) clearInterval(driveTimerRef.current);
    driveTimerRef.current = setInterval(() => {
      if (statusRef.current !== "on_duty") return;
      setDriveTimeSec((prev) => {
        const next = prev + 1;
        driveTimeRef.current = next;
        return next;
      });
    }, 1000);

    watchIdRef.current = navigator.geolocation.watchPosition(
      async (position) => {
        setTrackingPermission("granted");
        setIsTracking(true);
        setLastSeenAt(new Date());
        const coords = position.coords;
        lastKnownCoordsRef.current = {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: coords.accuracy,
          heading: coords.heading,
          altitude: coords.altitude,
        };
        currentPositionRef.current = { latitude: coords.latitude, longitude: coords.longitude };
        setCurrentPosition(currentPositionRef.current);
        setMapViewState((prev) => ({
          ...prev,
          latitude: coords.latitude,
          longitude: coords.longitude,
          zoom: Math.max(prev.zoom, 10.5),
        }));

        const point = { latitude: coords.latitude, longitude: coords.longitude };
        let speedMph = 0;
        if (typeof coords.speed === "number" && !Number.isNaN(coords.speed) && coords.speed >= 0) {
          speedMph = coords.speed * 2.23694;
        } else if (lastCoordRef.current) {
          const segmentMiles = haversineMiles(lastCoordRef.current, point);
          const elapsedSec = (position.timestamp - (lastCoordRef.current.timestamp || position.timestamp)) / 1000;
          if (elapsedSec > 0) speedMph = (segmentMiles / elapsedSec) * 3600;
        }
        setLiveSpeedMph(speedMph);

        if (statusRef.current === "on_duty" && lastCoordRef.current) {
          const segmentMiles = haversineMiles(lastCoordRef.current, point);
          if (segmentMiles > 0.001) {
            milesRef.current += segmentMiles;
            setMilesDriven(Number(milesRef.current.toFixed(2)));
          }
        }

        lastCoordRef.current = { ...point, timestamp: position.timestamp };
        await sendLocationUpdate(coords, Number(speedMph.toFixed(1)));
      },
      (error) => {
        setTrackingPermission("denied");
        stopTracking();
        toast.error(error.message || "Location permission denied");
      },
      {
        enableHighAccuracy: true,
        maximumAge: 1000,
        timeout: 10000,
      },
    );
  }, [sendLocationUpdate, stopTracking]);

  useEffect(() => {
    return () => {
      if (watchIdRef.current !== null) navigator.geolocation.clearWatch(watchIdRef.current);
      if (driveTimerRef.current) clearInterval(driveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!user?.id) return;
    try {
      const raw = localStorage.getItem(getTrackingStorageKey(user.id));
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved?.version !== TRACKING_STORAGE_VERSION) return;
      if (saved.currentStatus && responderStatusConfig[saved.currentStatus]) setCurrentStatus(saved.currentStatus);
      const statusFromKey = localStorage.getItem(getStatusStorageKey(user.id));
      if (statusFromKey && responderStatusConfig[statusFromKey]) setCurrentStatus(statusFromKey);
      if (saved.trackingPermission) setTrackingPermission(saved.trackingPermission);
      if (typeof saved.driveTimeSec === "number") {
        const driveSec = Math.max(0, Math.floor(saved.driveTimeSec));
        setDriveTimeSec(driveSec);
        driveTimeRef.current = driveSec;
      }
      if (typeof saved.milesDriven === "number") {
        const miles = Math.max(0, saved.milesDriven);
        setMilesDriven(miles);
        milesRef.current = miles;
      }
      if (typeof saved.liveSpeedMph === "number") setLiveSpeedMph(saved.liveSpeedMph);
      if (saved.lastSeenAt) setLastSeenAt(new Date(saved.lastSeenAt));
      if (saved.sessionStartedAt) setSessionStartedAt(saved.sessionStartedAt);
      if (saved.lastCoord) lastCoordRef.current = saved.lastCoord;
      if (saved.lastKnownCoords) {
        lastKnownCoordsRef.current = saved.lastKnownCoords;
        currentPositionRef.current = { latitude: saved.lastKnownCoords.latitude, longitude: saved.lastKnownCoords.longitude };
        setCurrentPosition(currentPositionRef.current);
        setMapViewState((prev) => ({
          ...prev,
          latitude: saved.lastKnownCoords.latitude,
          longitude: saved.lastKnownCoords.longitude,
          zoom: Math.max(prev.zoom, 10.5),
        }));
      }
      if (saved.isTracking) {
        setIsTracking(true);
        autoPromptedRef.current = true;
        startTracking({ resetSession: false });
      }
    } catch (_) {
      // Ignore corrupted local state.
    }
  }, [startTracking, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    const payload = {
      version: TRACKING_STORAGE_VERSION,
      currentStatus,
      isTracking,
      trackingPermission,
      liveSpeedMph,
      driveTimeSec,
      milesDriven,
      lastSeenAt: lastSeenAt ? new Date(lastSeenAt).toISOString() : null,
      sessionStartedAt,
      lastCoord: lastCoordRef.current,
      lastKnownCoords: lastKnownCoordsRef.current,
    };
    localStorage.setItem(getTrackingStorageKey(user.id), JSON.stringify(payload));
  }, [currentStatus, driveTimeSec, isTracking, lastSeenAt, liveSpeedMph, milesDriven, sessionStartedAt, trackingPermission, user?.id]);

  useEffect(() => {
    if (autoPromptedRef.current || isTracking) return;
    autoPromptedRef.current = true;
    startTracking({ resetSession: false });
  }, [isTracking, startTracking]);

  const sendEmergencyAction = useCallback((detail) => {
    window.dispatchEvent(new CustomEvent("eagle-emergency-action", { detail }));
  }, []);

  const updateIncidentStatus = async (reportId, status) => {
    setActionId(reportId);
    try {
      const response = await axios.put(`${API}/emergency/reports/${reportId}/respond`, {
        status,
        response_notes: responseDrafts[reportId] || null,
      });
      const updated = response.data;
      setEmergencyReports((prev) => prev.map((report) => (report.id === reportId ? updated : report)));
      toast.success(`Incident ${status}`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Failed to update incident");
    } finally {
      setActionId("");
    }
  };

  const activeReports = useMemo(() => emergencyReports.filter((report) => report.status !== "resolved"), [emergencyReports]);
  const acknowledgedReports = useMemo(() => emergencyReports.filter((report) => report.status === "acknowledged"), [emergencyReports]);
  const resolvedReports = useMemo(() => emergencyReports.filter((report) => report.status === "resolved"), [emergencyReports]);
  const statusConfig = responderStatusConfig[currentStatus] || responderStatusConfig.off_duty;
  const recentRoutes = emergencyRoutes.slice(0, 5);
  const nearbyServices = emergencyServices.slice(0, 6);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar role="emergency" />
      <main className="lg:ml-64 p-4 lg:p-8 space-y-6" data-testid="emergency-dashboard">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-heading font-bold tracking-tight text-orange-100">Emergency Response Dashboard</h1>
            <p className="mt-1 text-muted-foreground">Live responder status, incident reporting, route support, and admin-visible response tracking.</p>
          </div>
          <div className="flex items-center gap-2">
            <DashboardDateTimeWidget />
            <Button variant="outline" className="border-orange-500/40 text-orange-100" onClick={() => fetchEmergencyData({ force: true })}>
              Refresh
            </Button>
            <Button className="bg-orange-600 hover:bg-orange-500 text-black" onClick={() => sendEmergencyAction({ action: "open-tools" })}>
              Open GPS
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <Card className="card-hover overflow-hidden">
            <div className={`h-2 ${currentStatus === "available" ? "bg-emerald-500" : currentStatus === "on_duty" ? "bg-primary" : "bg-zinc-600"}`} />
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`flex h-14 w-14 items-center justify-center rounded-full ${statusConfig.bg}`}>
                  <statusConfig.icon className={`h-7 w-7 ${statusConfig.color}`} />
                </div>
                <div>
                  <p className="text-sm uppercase tracking-wider text-muted-foreground">Responder Status</p>
                  <p className={`text-2xl font-heading font-bold ${statusConfig.color}`}>{statusConfig.label}</p>
                </div>
              </div>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {Object.entries(responderStatusConfig).map(([key, config]) => (
                  <Button
                    key={key}
                    variant={currentStatus === key ? "default" : "outline"}
                    className={currentStatus === key ? "bg-primary" : ""}
                    onClick={() => updateResponderStatus(key)}
                    disabled={isUpdating}
                  >
                    <config.icon className="mr-2 h-4 w-4" />
                    {config.label}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2 text-muted-foreground">
                <Gauge className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wider">Live Tracking</span>
              </div>
              <div className="grid grid-cols-3 gap-3 text-center">
                <div>
                  <p className="text-2xl font-heading font-bold">{liveSpeedMph.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">mph</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{formatDriveTime(driveTimeSec)}</p>
                  <p className="text-xs text-muted-foreground">on duty</p>
                </div>
                <div>
                  <p className="text-2xl font-heading font-bold">{milesDriven.toFixed(2)}</p>
                  <p className="text-xs text-muted-foreground">mi</p>
                </div>
              </div>
              <div className="mt-4 text-sm text-muted-foreground">
                <p>Permission: <span className="font-semibold capitalize text-foreground">{trackingPermission}</span></p>
                {lastSeenAt && <p className="mt-1">Last update: {lastSeenAt.toLocaleTimeString()}</p>}
                {sessionStartedAt && <p className="mt-1">Session started: {new Date(sessionStartedAt).toLocaleTimeString()}</p>}
              </div>
              <div className="mt-4 flex gap-2">
                {!isTracking ? (
                  <Button className="bg-primary hover:bg-primary/90" onClick={() => startTracking()}>
                    Start Tracking
                  </Button>
                ) : (
                  <Button variant="outline" onClick={stopTracking}>
                    Stop Tracking
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2 text-muted-foreground">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wider">Incidents</span>
              </div>
              <p className="text-3xl font-heading font-bold">{activeReports.length}</p>
              <p className="mt-1 text-sm text-muted-foreground">Open incidents needing response</p>
              <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border p-3">
                  <p className="text-muted-foreground">Acknowledged</p>
                  <p className="text-xl font-heading font-bold">{acknowledgedReports.length}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-muted-foreground">Resolved</p>
                  <p className="text-xl font-heading font-bold">{resolvedReports.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="mb-4 flex items-center gap-2 text-muted-foreground">
                <ShieldCheck className="h-4 w-4" />
                <span className="text-xs uppercase tracking-wider">Coverage</span>
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-border p-3">
                  <p className="text-muted-foreground">Services</p>
                  <p className="text-2xl font-heading font-bold">{emergencyServices.length}</p>
                </div>
                <div className="rounded-lg border border-border p-3">
                  <p className="text-muted-foreground">My Routes</p>
                  <p className="text-2xl font-heading font-bold">{emergencyRoutes.length}</p>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between rounded-lg border border-border px-3 py-2 text-sm">
                <span>Safe Route Mode</span>
                <Switch checked={safeMode} onCheckedChange={setSafeMode} />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <Card className="xl:col-span-2 card-hover overflow-hidden">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <MapPin className="h-5 w-5 text-primary" />
                Live Responder Map
              </CardTitle>
              <CardDescription>Your live position, nearby emergency services, and active incident markers for rapid routing.</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative overflow-hidden rounded-xl border border-border">
                <MapView
                  mapboxAccessToken={MAPBOX_TOKEN}
                  mapLib={mapboxgl}
                  mapStyle={MAP_STYLE}
                  latitude={mapViewState.latitude}
                  longitude={mapViewState.longitude}
                  zoom={mapViewState.zoom}
                  onMove={(evt) => setMapViewState(evt.viewState)}
                  style={{ width: "100%", height: "min(52vh, 480px)" }}
                >
                  <NavigationControl position="top-left" />
                  {currentPosition && (
                    <Marker longitude={currentPosition.longitude} latitude={currentPosition.latitude} anchor="bottom">
                      <div
                        className="h-4 w-4 rounded-full border-2 border-white"
                        style={{
                          backgroundColor: responderMapMarkerColor[currentStatus] || "#94a3b8",
                          boxShadow: `0 0 14px ${responderMapMarkerColor[currentStatus] || "#94a3b8"}`,
                        }}
                      />
                    </Marker>
                  )}
                  {nearbyServices.map((service) => (
                    <Marker key={service.id} longitude={Number(service.longitude)} latitude={Number(service.latitude)} anchor="center">
                      <button
                        type="button"
                        className={getEmergencyServiceMarkerClass(service.service_type, service.status)}
                        onClick={() => setSelectedMapFeature({ ...service, feature_kind: "service" })}
                      >
                        <span className="text-[10px] font-bold">{getEmergencyServiceTypeLabel(service.service_type).slice(0, 1)}</span>
                      </button>
                    </Marker>
                  ))}
                  {activeReports.slice(0, 12).map((report) => (
                    <Marker key={report.id} longitude={Number(report.longitude)} latitude={Number(report.latitude)} anchor="center">
                      <button
                        type="button"
                        className={getEmergencyReportMarkerClass(report.severity, report.status)}
                        onClick={() => setSelectedMapFeature({ ...report, feature_kind: "report" })}
                      >
                        <AlertTriangle className="h-3.5 w-3.5" />
                      </button>
                    </Marker>
                  ))}
                  {selectedMapFeature && (
                    <Popup
                      longitude={Number(selectedMapFeature.longitude)}
                      latitude={Number(selectedMapFeature.latitude)}
                      anchor="bottom"
                      offset={18}
                      closeOnClick={false}
                      onClose={() => setSelectedMapFeature(null)}
                    >
                      <div className="min-w-[240px] space-y-3 p-2 text-xs text-stone-900">
                        {selectedMapFeature.feature_kind === "service" ? (
                          <>
                            <div>
                              <p className="text-sm font-semibold">{selectedMapFeature.name}</p>
                              <p className="text-stone-600">{getEmergencyServiceTypeLabel(selectedMapFeature.service_type)}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge className={getEmergencyServiceBadgeClass(selectedMapFeature.status)}>
                                {getEmergencyServiceStatusLabel(selectedMapFeature.status)}
                              </Badge>
                              {selectedMapFeature.distance_miles !== undefined && (
                                <Badge variant="outline">{formatEmergencyDistance(selectedMapFeature.distance_miles)}</Badge>
                              )}
                            </div>
                            <p>{selectedMapFeature.address}</p>
                            <Button
                              size="sm"
                              className="w-full bg-orange-600 text-black hover:bg-orange-500"
                              onClick={() => sendEmergencyAction({ action: "route-service", serviceId: selectedMapFeature.id, safeMode })}
                            >
                              Route In GPS
                            </Button>
                          </>
                        ) : (
                          <>
                            <div>
                              <p className="text-sm font-semibold">{getEmergencyReportTypeLabel(selectedMapFeature.report_type)}</p>
                              <p className="text-stone-600">{selectedMapFeature.description}</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              <Badge className={getEmergencySeverityBadgeClass(selectedMapFeature.severity)}>
                                {getEmergencySeverityLabel(selectedMapFeature.severity)}
                              </Badge>
                              <Badge className={getEmergencyReportStatusBadgeClass(selectedMapFeature.status)}>
                                {getEmergencyReportStatusLabel(selectedMapFeature.status)}
                              </Badge>
                            </div>
                            <Button
                              size="sm"
                              className="w-full bg-orange-600 text-black hover:bg-orange-500"
                              onClick={() => sendEmergencyAction({ action: "route-report", reportId: selectedMapFeature.id, safeMode })}
                            >
                              Route To Incident
                            </Button>
                          </>
                        )}
                      </div>
                    </Popup>
                  )}
                </MapView>
                <div className="absolute right-3 top-3 z-10 flex flex-col gap-2">
                  <Button size="sm" variant="outline" className="border-stone-700 bg-black/75 text-stone-100" onClick={() => currentPosition && setMapViewState((prev) => ({ ...prev, latitude: currentPosition.latitude, longitude: currentPosition.longitude, zoom: Math.max(prev.zoom, 11) }))}>
                    <LocateFixed className="mr-2 h-4 w-4" />
                    Recenter
                  </Button>
                  <Button size="sm" className="bg-orange-600 text-black hover:bg-orange-500" onClick={() => sendEmergencyAction({ action: "report-incident" })}>
                    Report Incident
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="card-hover">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Timer className="h-5 w-5 text-primary" />
                On-Duty History
              </CardTitle>
              <CardDescription>Accumulated responder activity while marked On Duty.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-border p-4 bg-muted/30">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Total</p>
                  <p className="text-xl font-heading font-bold">{formatDriveTime(driveHistory.summary?.total_drive_time_seconds || 0)}</p>
                </div>
                <div className="rounded-lg border border-border p-4 bg-muted/30">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Today</p>
                  <p className="text-xl font-heading font-bold">{formatDriveTime(driveHistory.summary?.today_drive_time_seconds || 0)}</p>
                </div>
              </div>
              <div className="space-y-2">
                {(driveHistory.sessions || []).slice(0, 6).map((session) => (
                  <div key={session.id} className="rounded-lg border border-border p-3 text-sm">
                    <p className="font-semibold">{formatDriveTime(session.duration_seconds || 0)}</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {session.session_started_at ? new Date(session.session_started_at).toLocaleString() : "-"} to {session.session_ended_at ? new Date(session.session_ended_at).toLocaleString() : "-"}
                    </p>
                  </div>
                ))}
                {(driveHistory.sessions || []).length === 0 && (
                  <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">No completed on-duty sessions yet.</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-5 xl:grid-cols-3">
          <Card className="xl:col-span-2 card-hover">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <LifeBuoy className="h-5 w-5 text-primary" />
                Incident Queue
              </CardTitle>
              <CardDescription>Acknowledge or resolve incidents and push the response into the admin reporting flow.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {dashboardLoading ? (
                <p className="text-sm text-muted-foreground">Loading emergency incidents...</p>
              ) : activeReports.length === 0 ? (
                <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">No active incidents are waiting for response.</p>
              ) : (
                activeReports.slice(0, 10).map((report) => (
                  <div key={report.id} className="rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-base font-heading font-semibold">{getEmergencyReportTypeLabel(report.report_type)}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{report.description}</p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Badge className={getEmergencySeverityBadgeClass(report.severity)}>{getEmergencySeverityLabel(report.severity)}</Badge>
                        <Badge className={getEmergencyReportStatusBadgeClass(report.status)}>{getEmergencyReportStatusLabel(report.status)}</Badge>
                      </div>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3 text-sm text-muted-foreground">
                      <p>Reporter: <span className="text-foreground">{report.user_name || "-"}</span></p>
                      <p>Location: <span className="text-foreground">{Number(report.latitude).toFixed(4)}, {Number(report.longitude).toFixed(4)}</span></p>
                      <p>Created: <span className="text-foreground">{report.created_at ? new Date(report.created_at).toLocaleString() : "-"}</span></p>
                    </div>
                    <Textarea
                      value={responseDrafts[report.id] ?? report.response_notes ?? ""}
                      onChange={(event) => setResponseDrafts((prev) => ({ ...prev, [report.id]: event.target.value }))}
                      placeholder="Response notes for dispatch/admin tracking"
                      rows={3}
                    />
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" className="border-orange-500/40 text-orange-100" onClick={() => sendEmergencyAction({ action: "route-report", reportId: report.id, safeMode })}>
                        <Navigation className="mr-2 h-4 w-4" />
                        Route In GPS
                      </Button>
                      <Button
                        className="bg-orange-600 text-black hover:bg-orange-500"
                        onClick={() => updateIncidentStatus(report.id, "acknowledged")}
                        disabled={actionId === report.id || report.status === "acknowledged"}
                      >
                        {actionId === report.id ? "Saving..." : "Acknowledge"}
                      </Button>
                      <Button
                        variant="outline"
                        className="border-emerald-500/40 text-emerald-300"
                        onClick={() => updateIncidentStatus(report.id, "resolved")}
                        disabled={actionId === report.id || report.status === "resolved"}
                      >
                        {actionId === report.id ? "Saving..." : "Resolve"}
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <div className="space-y-5">
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <ShieldCheck className="h-5 w-5 text-primary" />
                  Nearby Services
                </CardTitle>
                <CardDescription>Closest emergency service coverage relative to your live position.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {nearbyServices.map((service) => (
                  <button
                    key={service.id}
                    type="button"
                    className="w-full rounded-lg border border-border bg-muted/20 px-3 py-3 text-left hover:border-orange-500/40"
                    onClick={() => sendEmergencyAction({ action: "route-service", serviceId: service.id, safeMode })}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">{service.name}</p>
                      <Badge className={getEmergencyServiceBadgeClass(service.status)}>{getEmergencyServiceStatusLabel(service.status)}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{getEmergencyServiceTypeLabel(service.service_type)} · {formatEmergencyDistance(service.distance_miles)}</p>
                  </button>
                ))}
                {nearbyServices.length === 0 && (
                  <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">No nearby services available yet.</p>
                )}
              </CardContent>
            </Card>

            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="font-heading flex items-center gap-2">
                  <RouteIcon className="h-5 w-5 text-primary" />
                  Recent Routes
                </CardTitle>
                <CardDescription>Saved emergency routes generated from your responder account.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {recentRoutes.map((route) => (
                  <div key={route.id} className="rounded-lg border border-border bg-muted/20 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="font-medium">{route.service_name}</p>
                      <Badge variant="outline">{route.safe_route ? "Safe" : "Priority"}</Badge>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">{getEmergencyServiceTypeLabel(route.service_type)} · {formatEmergencyDistance(route.distance_miles)} · {formatEmergencyEta(route.duration_seconds)}</p>
                  </div>
                ))}
                {recentRoutes.length === 0 && (
                  <p className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">No emergency routes saved yet.</p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default EmergencyDashboard;
